import { ExternalLink, FileText } from 'lucide-react';
import { useEffect } from 'react';
import type { AnnouncementAttachment } from '../api/announcements';
import { documentKindLabel, formatByteSize, msUntilResign } from '../lib/attachments';

/**
 * Keeps every document link on a noticeboard live while the page stays open — TROOP-255.
 *
 * A document's URL is signed for five minutes and the noticeboard is a page people leave open for
 * an afternoon, so without this a parent who reads the card, makes a cup of tea and taps *Medical
 * form* gets the store's expired-signature page instead of the form. `EventGallery` solves the same
 * problem by re-listing when an image fails; a link to a new tab has no failure to hear, so this
 * re-lists **before** the soonest signature lapses instead — and again on returning to a tab whose
 * timer a phone suspended. Refreshing on the tap would not do: a tab opened after an await is a
 * popup the browser blocks.
 *
 * `refresh` should re-read the announcements alone, not the whole screen.
 */
export function useKeepAttachmentsSigned(
  announcements: readonly { attachments: AnnouncementAttachment[] }[] | null,
  refresh: () => void,
) {
  useEffect(() => {
    const expiries = (announcements ?? []).flatMap((a) =>
      a.attachments.flatMap((x) => (x.file ? [x.file.expiresAt] : [])),
    );

    const wait = msUntilResign(expiries, Date.now());

    if (wait === null) {
      return;
    }

    const due = Date.now() + wait;
    const timer = window.setTimeout(refresh, wait);

    // A backgrounded phone tab can sleep through the timer; coming back past it re-lists at once.
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() >= due) {
        refresh();
      }
    };

    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [announcements, refresh]);
}

/**
 * What an announcement hands out, below its body and in its author's order — TROOP-255.
 *
 * SPEC-GAP: §6's announcement card names no attachment row. Built from existing tokens only — the
 * card's own surface with a `--color-border-subtle` hairline, `--radius-md`, the type steps — and
 * the icons the requirement kinds already use for the same two things (`FileText` for a document,
 * `ExternalLink` for an address somewhere else).
 *
 * One component for both doors: `AnnouncementCard` on the unit's page and `FamilyScreen`'s row on
 * a parent's. A guardian reads everything a member does here, so a second rendering would only be
 * a place for one door to quietly lose the other's handouts.
 *
 * **A link shows the host it goes to, in full, beside its label** — the requirement's external
 * link has carried the same line since §7, and for the same reason: a label is whatever the person
 * typed, and the host is the only part that says where a tap actually lands. It wraps rather than
 * truncating, because an ellipsis is exactly where `council.org.example.com` hides its last word.
 *
 * Nothing here comes from the body. The body is `CuratedText` and draws its own links; a card is a
 * separate thing an officer chose to hand out, and a document goes in as one of these.
 */
export function AnnouncementAttachments({
  attachments,
}: {
  attachments: AnnouncementAttachment[];
}) {
  if (attachments.length === 0) {
    return null;
  }

  return (
    <ul
      aria-label="Attachments"
      className="flex flex-col"
      style={{ listStyle: 'none', margin: 'var(--space-3) 0 0', padding: 0, gap: 'var(--space-2)' }}
    >
      {attachments.map((attachment) => (
        <li
          key={attachment.id}
          className="flex items-start"
          style={{
            gap: 'var(--space-3)',
            border: '1px solid var(--color-border-subtle)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-1) var(--space-3)',
          }}
        >
          <AttachmentRow attachment={attachment} />
        </li>
      ))}
    </ul>
  );
}

function AttachmentRow({ attachment }: { attachment: AnnouncementAttachment }) {
  if (attachment.kind === 'Document' && attachment.file) {
    const file = attachment.file;

    return (
      <>
        <Icon kind="Document" />
        <div className="flex flex-wrap items-center" style={textColumn}>
          {/* A plain link to a new tab. The URL is signed, short-lived and cross-origin, so
              `download` would be ignored; the server names the download from the label. */}
          <a
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="t-body-strong"
            style={linkStyle}
          >
            {attachment.label}
          </a>
          <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
            {documentKindLabel(file.contentType)} &middot; {formatByteSize(file.byteSize)}
          </span>
        </div>
      </>
    );
  }

  if (attachment.kind === 'Link' && attachment.linkUrl && attachment.linkHost) {
    return (
      <>
        <Icon kind="Link" />
        <div className="flex flex-wrap items-center" style={textColumn}>
          <a
            href={attachment.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="t-body-strong"
            style={linkStyle}
          >
            {attachment.label}
          </a>
          {/* The destination, in full and never shortened. This is the whole mitigation. */}
          <span
            className="t-caption"
            style={{ color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}
          >
            goes to <strong>{attachment.linkHost}</strong>
          </span>
        </div>
      </>
    );
  }

  // A row the server sent without the half its kind needs. Drawing a label that goes nowhere would
  // be a control with nothing behind it, so it says so instead.
  return (
    <span
      className="t-caption"
      style={{ color: 'var(--color-text-muted)', paddingBlock: 'var(--space-2)' }}
    >
      {attachment.label} &mdash; not available
    </span>
  );
}

/**
 * The label and its caption share a wrapping row, so the caption sits beside the label when there
 * is room and drops beneath it when there is not. A real floor rather than `minWidth: 0`, which
 * lets a flex item collapse to nothing (`docs/frontend.md`).
 */
const textColumn: React.CSSProperties = {
  columnGap: 'var(--space-2)',
  minWidth: '6rem',
  flex: '1 1 8rem',
};

const linkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 'var(--tap-min)',
  overflowWrap: 'anywhere',
};

function Icon({ kind }: { kind: 'Document' | 'Link' }) {
  const Glyph = kind === 'Document' ? FileText : ExternalLink;

  return (
    <span
      className="inline-flex items-center shrink-0"
      style={{ minHeight: 'var(--tap-min)', color: 'var(--color-text-muted)' }}
    >
      <Glyph size={16} aria-label={kind === 'Document' ? 'Document' : 'Link'} />
    </span>
  );
}
